import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import InventoryIcon from '@mui/icons-material/Inventory2';
import DescriptionIcon from '@mui/icons-material/Description';
import LabelIcon from '@mui/icons-material/Label';
import GroupIcon from '@mui/icons-material/Group';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ArticleIcon from '@mui/icons-material/Article';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CategoryIcon from '@mui/icons-material/Category';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { TenantSwitcher } from '../TenantSwitcher';

export const SIDEBAR_WIDTH = 240;

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const tenantSections: NavSection[] = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', to: '/', icon: <DashboardIcon fontSize="small" /> }],
  },
  {
    label: 'Tenant data',
    items: [
      { label: 'Projects', to: '/projects', icon: <FolderIcon fontSize="small" /> },
      { label: 'Items', to: '/items', icon: <InventoryIcon fontSize="small" /> },
      { label: 'Item details', to: '/item-details', icon: <DescriptionIcon fontSize="small" /> },
    ],
  },
];

const globalSections: NavSection[] = [
  {
    label: 'Global catalog',
    items: [{ label: 'Tags', to: '/tags', icon: <LabelIcon fontSize="small" /> }],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Tenants', to: '/tenants', icon: <ApartmentIcon fontSize="small" /> },
      { label: 'Users', to: '/users', icon: <GroupIcon fontSize="small" /> },
    ],
  },
];

const globalSettingsSections: NavSection[] = [
  {
    label: 'Settings',
    items: [
      {
        label: 'Document Management',
        to: '/global-settings/document-management',
        icon: <ArticleIcon fontSize="small" />,
      },
      {
        label: 'AI Assistant Roles',
        to: '/global-settings/ai-assistant-roles',
        icon: <SmartToyIcon fontSize="small" />,
      },
      {
        label: 'Entity Type Skills',
        to: '/global-settings/entity-type-skills',
        icon: <PsychologyIcon fontSize="small" />,
      },
      {
        label: 'Entity Types',
        to: '/global-settings/entity-types',
        icon: <CategoryIcon fontSize="small" />,
      },
    ],
  },
];

export function AppSidebar() {
  const { pathname } = useLocation();
  const isGlobalSettings = pathname.startsWith('/global-settings');

  const renderSection = (section: NavSection) => (
    <Box key={section.label} sx={{ mb: 1 }}>
      <Typography
        variant="overline"
        sx={{ px: 2.5, color: 'text.secondary', display: 'block' }}
      >
        {section.label}
      </Typography>
      <List dense disablePadding>
        {section.items.map((entry) => {
          const selected =
            entry.to === '/'
              ? pathname === '/'
              : pathname === entry.to || pathname.startsWith(entry.to + '/');
          return (
            <ListItemButton
              key={entry.to}
              component={RouterLink}
              to={entry.to}
              selected={selected}
              sx={{ mx: 1, borderRadius: 1 }}
            >
              <ListItemIcon
                sx={{ minWidth: 32, color: selected ? 'primary.main' : 'text.secondary' }}
              >
                {entry.icon}
              </ListItemIcon>
              <ListItemText
                primary={entry.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: selected ? 600 : 400,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  if (isGlobalSettings) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 1 }}>
          {globalSettingsSections.map(renderSection)}
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto', py: 1 }}>
        <Box
          sx={{
            mx: 1,
            mt: 0.5,
            pt: 1,
            pb: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.18),
            backgroundImage: (t) =>
              `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.08)} 0%, ${alpha(
                t.palette.primary.main,
                0.03,
              )} 100%),` +
              `radial-gradient(${alpha(t.palette.primary.main, 0.08)} 1px, transparent 1px)`,
            backgroundSize: 'auto, 14px 14px',
            backgroundPosition: '0 0, 0 0',
            backgroundColor: 'background.paper',
            boxShadow: (t) => `inset 0 1px 0 ${alpha(t.palette.primary.main, 0.05)}`,
          }}
        >
          <Box sx={{ px: 1.5, pb: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
            >
              Active tenant
            </Typography>
            <TenantSwitcher fullWidth />
          </Box>
          {tenantSections.map(renderSection)}
        </Box>

        <Divider sx={{ my: 2, mx: 2 }} />

        {globalSections.map(renderSection)}
      </Box>
    </Drawer>
  );
}
